import type { UserDto } from '@issuemanager/api-client';
import {
  ExtendedAutocomplete,
  sortOptionsBySelected,
} from '@issuemanager/apps/client/components';
import {
  MAX_CHARACTERS_TO_SEARCH,
  MIN_CHARACTERS_TO_SEARCH,
  truncateSearchText,
} from '@issuemanager/apps/client/utils';
import { useInfiniteUsers } from '@issuemanager/client/features/users';
import { useState } from 'react';
import type { MultipleSelectorProps } from '../types';
import { useMultipleSelectorProps } from '../use-multiple-selector';
import { mergePaginatedDataAndValue } from '../utils';

export interface UserMultipleSelectorProps
  extends MultipleSelectorProps<UserDto[], UserDto> {
  issueTypeId?: string;
  issueSubtypeId?: string;
}

export function UserMultipleSelector(props: UserMultipleSelectorProps) {
  const {
    issueTypeId,
    issueSubtypeId,
    value,
    onChange,
    enabled: _enabled,
    ...rest
  } = props;

  const [filterByText, setFilterByText] = useState<string>('');
  const [partialValue, setPartialValue] = useState(value ?? []);
  const usersQuery = useInfiniteUsers({
    filter: {
      searchTerm: filterByText,
      issueType: issueTypeId,
      issueSubtype: issueSubtypeId,
    },
    enabled: filterByText.length >= MIN_CHARACTERS_TO_SEARCH,
    handleError: true,
  });

  const options = mergePaginatedDataAndValue(
    usersQuery.data?.pages.map((c) => c.data) ?? [],
    value ?? [],
  );

  const autocompleteProps = useMultipleSelectorProps({
    options,
    setInputValue: (text) => setFilterByText(truncateSearchText(text)),
    query: usersQuery,
    value,
    setValue: onChange,
    partialValue,
    setPartialValue,
  });

  return (
    <ExtendedAutocomplete
      {...autocompleteProps}
      options={sortOptionsBySelected(
        options,
        partialValue,
        autocompleteProps.isOptionEqualToValue,
      )}
      getOptionLabel={(option) => `${option.name} <${option.email}>`}
      {...rest}
      maxLength={MAX_CHARACTERS_TO_SEARCH}
    />
  );
}
